// Plan 08-07 — Delete account confirmation modal.
//
// Locale-aware destructive-phrase contract per DEC-022 + Phase 2 lock
// (apps/backend/src/account/delete.ts):
//   en (default) → "DELETE"
//   sv           → "RADERA"
// EXACT match required — no trim, no case fold (T-PHASE2-35). The CTA is
// disabled until the typed phrase matches exactly. The backend enforces
// the same lock; this UI mirrors it so the user sees a fail-closed state
// before submitting.
//
// On confirm: apiFetch('/api/account', 'DELETE', { body: { confirm_phrase } }).
// `apiFetch` (apps/web/src/net/client.ts) attaches the v1 vendor
// `Accept` + `Content-Type` headers (DEC-018) and the bearer from
// `$authBearer` automatically; it also targets the configured
// `VITE_API_BASE_URL` (the backend lives on a different Cloudflare
// Workers origin in production — never the Pages origin). On 204 (or
// `apiFetch`'s 2xx → undefined): clearAuth() + navigate to /splash.
// On 401 (handled inside apiFetch as AuthInvalidError + clearAuth):
// silently bounce via auth.logout() (oracle-hardened, no error code
// shown). On other failures: surface a generic inline error — voice
// locks say "no error codes to player".
//
// T-08-DEL-PHRASE mitigation: empty/wrong-phrase keeps CTA disabled →
// user can never accidentally fire the wipe; even if they bypass the
// disabled state via DOM tampering, the backend rejects the mismatch.
//
// Plan 09-03 (NY-09-DELETE-FLOW): on the 204 success branch we render an
// in-modal silent-deletion notice for ~3s before clearing auth + navigating
// to /splash. Copy is `account.delete.silent_notice` (key landed in Plan
// 09-02 in en.json + sv.json). The notice surface (data-testid
// 'silent-deletion-notice', role='status', aria-live='polite') is the
// in-product DEC-022 statement: "deletion is silent — verify by trying
// to sign in again". Playwright e2e account-deletion.spec.ts asserts the
// notice is visible after a 204 in both en + sv typed-confirmation flows.
//
// Plan 09-10 CR-02 fix: previously this modal called
// `fetch('/api/account', ...)` directly with hand-rolled headers — that
// targeted the Pages origin (relative URL) instead of
// `VITE_API_BASE_URL`, used `application/json` instead of the v1 vendor
// content-type, and bypassed the apiFetch 401 / 5xx contract handling.
// Production deletion would silently fail. Routed through apiFetch.

// ARCA-82 WS2 — migrated to <Dialog> primitive (scrim/panel/focus-trap live there).
import { useStore } from '@nanostores/react'
import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useAuth } from '../../auth/AuthProvider.js'
import { AuthInvalidError } from '../../auth/types.js'
import { ApiError, apiFetch } from '../../net/client.js'
import { useRouter } from '../../router/router.js'
import { $authBearer, clearAuth } from '../../stores/auth.js'
import { $locale } from '../../stores/locale.js'
import { Btn } from '../components/Btn.js'
import { Dialog } from '../components/Dialog.js'

interface DeleteAccountModalProps {
  open: boolean
  onClose: () => void
}

const PHRASES = { en: 'DELETE', sv: 'RADERA' } as const

export function DeleteAccountModal({
  open,
  onClose,
}: DeleteAccountModalProps): JSX.Element | null {
  const locale = useStore($locale)
  const bearer = useStore($authBearer)
  const auth = useAuth()
  const router = useRouter()
  const intl = useIntl()
  const [typed, setTyped] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Plan 09-03 (NY-09-DELETE-FLOW): true once the backend returns 204; the
  // form is replaced by the silent-deletion notice for ~3s before we clear
  // auth + navigate to /splash.
  const [showSilentNotice, setShowSilentNotice] = useState(false)

  if (!open) return null

  const expectedPhrase = PHRASES[locale]
  const placeholderId =
    locale === 'sv'
      ? 'account.delete.confirm.input.placeholder.sv'
      : 'account.delete.confirm.input.placeholder.en'
  const placeholderDefault = locale === 'sv' ? 'Skriv RADERA' : 'Type DELETE'
  const phraseMatches = typed === expectedPhrase

  async function handleConfirm(): Promise<void> {
    if (!phraseMatches || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      if (!bearer) {
        // Edge case: bearer cleared mid-modal. Bail to splash so the user
        // re-authenticates cleanly.
        auth.logout()
        return
      }
      // CR-02 (Plan 09-10): apiFetch attaches Bearer + v1 vendor
      // content-type and targets VITE_API_BASE_URL. 204 → resolves to
      // `undefined`. 401 → throws AuthInvalidError (apiFetch already
      // called clearAuth). Other 4xx/5xx → throws ApiError(status, body).
      // Network error / 5xx after one retry → throws ApiError(0, ...).
      await apiFetch('/api/account', 'DELETE', {
        body: { confirm_phrase: expectedPhrase },
      })
      // Plan 09-03 (NY-09-DELETE-FLOW): show the in-modal silent-deletion
      // notice for ~3s so the user reads "deletion is silent — verify by
      // trying to sign in again" (DEC-022) before we clear auth + bounce
      // to /splash. The defer makes the notice testable in Playwright.
      setShowSilentNotice(true)
      setTimeout(() => {
        clearAuth()
        onClose()
        router.navigate('/splash')
      }, 3000)
    } catch (e) {
      if (e instanceof AuthInvalidError) {
        // 401 — apiFetch already cleared auth. Bounce to splash silently
        // (oracle-hardened — no distinguishable error to player).
        auth.logout()
        return
      }
      if (e instanceof ApiError && e.status === 0) {
        // Network error — connection-lost copy.
        setError(
          intl.formatMessage({
            id: 'error.connection_lost',
            defaultMessage: "You're offline. Your move is queued.",
          }),
        )
      } else {
        // 4xx/5xx — surface a generic inline error; never expose the code
        // (UI-SPEC §"No error codes shown to player").
        setError(
          intl.formatMessage({
            id: 'error.server_error.body',
            defaultMessage: 'The runes are slow today. Try again in a moment.',
          }),
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + panel + focus trap + dismissal
    // live in <Dialog>/ModalScrim. padding:0 so the crimson header band bleeds
    // to the rounded panel edge (overflow:hidden clips it).
    // rg-screen/modal-shroud dropped: rg-screen adds a sky gradient (not wanted
    // on a modal scrim), modal-shroud is a dead class undefined in any CSS file.
    // Destructive guard: dismissOnScrimTap blocks accidental close during submit
    // AND during the post-204 silent-deletion notice.
    <Dialog
      onClose={onClose}
      testId="delete-account-modal"
      labelledBy="delete-account-title"
      dismissOnScrimTap={!submitting && !showSilentNotice}
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      {/* Header band — Crimson tint to flag destructive intent. */}
      <div
        style={{
          minHeight: 'var(--space-6)',
          padding: 'var(--space-3) var(--space-4)',
          backgroundColor: 'var(--color-surface-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '3px solid var(--color-cta-destructive)',
        }}
      >
        <h2
          id="delete-account-title"
          style={{
            margin: 0,
            fontFamily: 'Fredoka, sans-serif',
            fontWeight: 700,
            fontSize: 'var(--font-size-18, 18px)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="account.delete.confirm.title"
            defaultMessage="Delete account"
          />
        </h2>
      </div>
      <div style={{ padding: 'var(--space-4)' }}>
        {showSilentNotice ? (
          // Plan 09-03 NY-09-DELETE-FLOW: post-204 silent-deletion notice.
          // Renders <FormattedMessage id="account.delete.silent_notice" />
          // from en.json + sv.json (key landed in Plan 09-02). Replaces the
          // form so the user cannot click "delete again".
          // Theme-agnostic — uses the launch CSS-var theme system (no
          // per-theme overrides needed across the four launch palettes).
          <div
            data-testid="silent-deletion-notice"
            role="status"
            aria-live="polite"
            style={{
              padding: 'var(--space-4)',
              textAlign: 'center',
              backgroundColor: 'var(--color-bg-mid)',
              color: 'var(--color-text-primary)',
              fontFamily: 'Manrope, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 'var(--font-size-15, 15px)',
              lineHeight: 1.6,
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <FormattedMessage
              id="account.delete.silent_notice"
              defaultMessage="Deletion is silent — verify by attempting to sign in again. After deletion, your account no longer exists."
            />
          </div>
        ) : (
          <>
            <p
              style={{
                margin: '0 0 var(--space-4)',
                fontFamily: 'Manrope, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 'var(--font-size-14)',
                lineHeight: 1.5,
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage
                id="account.delete.confirm.body"
                defaultMessage="Type DELETE to confirm. This cannot be undone. Your data is purged within 30 days. Verify by trying to sign in again — we have no email."
              />
            </p>
            <input
              data-testid="delete-account-confirm-input"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={intl.formatMessage({
                id: placeholderId,
                defaultMessage: placeholderDefault,
              })}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={submitting}
              style={{
                width: '100%',
                minHeight: '48px',
                padding: '0 var(--space-4)',
                borderRadius: 'var(--radius-pill)',
                border: '3px solid var(--color-surface-stroke)',
                backgroundColor: 'var(--color-surface-deep)',
                color: 'var(--color-text-primary)',
                fontFamily: 'Manrope, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                letterSpacing: '0.05em',
                boxSizing: 'border-box',
              }}
            />
            {error !== null && (
              <p
                role="alert"
                data-testid="delete-account-error"
                style={{
                  marginTop: 'var(--space-3)',
                  marginBottom: 0,
                  fontFamily: 'Manrope, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-13)',
                  color: 'var(--color-cta-destructive)',
                }}
              >
                {error}
              </p>
            )}
            <div
              style={{
                marginTop: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
              }}
            >
              {/* Kept as a raw <button> on the take-two recipe classes: it
                  exposes a test-asserted `data-disabled` state (e2e +
                  DeleteAccountModal.test) that the typed Btn primitive does
                  not model. Not a reimplementation — it consumes `.btn`. */}
              <button
                type="button"
                className="btn btn-primary btn-lg btn-block"
                onClick={handleConfirm}
                disabled={!phraseMatches || submitting}
                data-cta="delete-account-confirm"
                data-disabled={!phraseMatches || submitting}
              >
                <FormattedMessage
                  id="account.delete.confirm.cta_destructive"
                  defaultMessage="Delete account permanently"
                />
              </button>
              <Btn
                kind="ghost"
                size="md"
                block
                onClick={onClose}
                disabled={submitting}
                testId="delete-account-cancel"
              >
                <FormattedMessage
                  id="account.delete.confirm.cta_cancel"
                  defaultMessage="Keep my account"
                />
              </Btn>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
